/**
 * ClickReel SDK — Global Morph Keyframes
 *
 * Injects CSS @keyframes into document.head (not Shadow DOM) because
 * morph containers, glow elements, and particles render on document.body.
 * One-time injection guarded by element ID.
 */

export function ensureGlobalMorphKeyframes(): void {
  if (document.getElementById('cr-morph-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'cr-morph-keyframes';
  style.textContent = `
    /* ===== Morph transition animations ===== */
    @keyframes cr-morph-portal {
      0%   { clip-path: circle(150vh at 50% 50%); opacity: 1; }
      60%  { clip-path: circle(80px at var(--morph-x) var(--morph-y)); opacity: 1; }
      80%  { clip-path: circle(42px at var(--morph-x) var(--morph-y)); opacity: 0.95; }
      90%  { clip-path: circle(30px at var(--morph-x) var(--morph-y)); opacity: 0.9; }
      100% { clip-path: circle(var(--morph-radius, 36px) at var(--morph-x) var(--morph-y)); opacity: 0; }
    }
    @keyframes cr-morph-slide {
      0%   { transform: translate(0, 0) scale(1); opacity: 1; }
      70%  { transform: translate(var(--morph-tx, 0px), var(--morph-ty, 0px)) scale(0.15); opacity: 0.9; }
      100% { transform: translate(var(--morph-tx, 0px), var(--morph-ty, 0px)) scale(0.05); opacity: 0; }
    }
    @keyframes cr-morph-spring {
      0%   { transform: scale(0.85); }
      40%  { transform: scale(1.12); }
      60%  { transform: scale(0.95); }
      80%  { transform: scale(1.02); }
      100% { transform: scale(1); }
    }
    @keyframes cr-morph-glow {
      0%   { box-shadow: 0 0 0 0 var(--morph-color, rgba(99,102,241,0.6)); opacity: 1; }
      100% { box-shadow: 0 0 40px 20px var(--morph-color, rgba(99,102,241,0)); opacity: 0; }
    }
    @keyframes cr-morph-glow-outer {
      0%   { box-shadow: 0 0 0 0 var(--morph-color, rgba(99,102,241,0.3)); opacity: 1; }
      100% { box-shadow: 0 0 60px 30px var(--morph-color, rgba(99,102,241,0)); opacity: 0; }
    }

    /* ===== Attention-snap entrance animations ===== */
    @keyframes attsnap-shake-mild {
      0%, 100% { transform: rotate(0deg) translateX(0); }
      10% { transform: rotate(-2deg) translateX(-2px); }
      20% { transform: rotate(2deg) translateX(2px); }
      30% { transform: rotate(-3deg) translateX(-3px); }
      40% { transform: rotate(3deg) translateX(3px); }
      50% { transform: rotate(-2deg) translateX(-2px); }
      60% { transform: rotate(2deg) translateX(2px); }
      70% { transform: rotate(-1deg) translateX(-1px); }
      80% { transform: rotate(1deg) translateX(1px); }
      90% { transform: rotate(0deg); }
    }
    @keyframes attsnap-shake-medium {
      0%, 100% { transform: rotate(0deg) translateX(0); }
      10% { transform: rotate(-5deg) translateX(-4px); }
      20% { transform: rotate(6deg) translateX(5px); }
      30% { transform: rotate(-8deg) translateX(-7px); }
      40% { transform: rotate(8deg) translateX(7px); }
      50% { transform: rotate(-6deg) translateX(-5px); }
      60% { transform: rotate(5deg) translateX(4px); }
      70% { transform: rotate(-3deg) translateX(-3px); }
      80% { transform: rotate(2deg) translateX(2px); }
      90% { transform: rotate(-1deg); }
    }
    @keyframes attsnap-shake-wild {
      0%, 100% { transform: rotate(0deg) translateX(0) scale(1); }
      8%  { transform: rotate(-10deg) translateX(-8px) scale(1.04); }
      16% { transform: rotate(13deg)  translateX(10px) scale(1.06); }
      24% { transform: rotate(-14deg) translateX(-11px) scale(1.05); }
      32% { transform: rotate(14deg)  translateX(11px) scale(1.06); }
      40% { transform: rotate(-11deg) translateX(-8px) scale(1.03); }
      48% { transform: rotate(9deg)   translateX(7px)  scale(1.02); }
      56% { transform: rotate(-6deg)  translateX(-5px) scale(1.01); }
      64% { transform: rotate(5deg)   translateX(4px); }
      72% { transform: rotate(-3deg)  translateX(-2px); }
      80% { transform: rotate(2deg)   translateX(1px); }
      90% { transform: rotate(-1deg); }
    }
    @keyframes attsnap-appear {
      from { opacity: 0; transform: scale(0.72); }
      to   { opacity: 1; transform: scale(1); }
    }
    @keyframes attsnap-settled-bounce {
      0%   { transform: scale(1); }
      30%  { transform: scale(1.08); }
      60%  { transform: scale(0.96); }
      80%  { transform: scale(1.02); }
      100% { transform: scale(1); }
    }

    /* ===== Reduced motion ===== */
    @media (prefers-reduced-motion: reduce) {
      [style*="cr-morph"], [style*="attsnap"] {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}
